import { usePreferences } from '../context/PreferencesContext';
import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../css/BottomLeftNoteTaker.css';
import { useNote } from '../context/NoteContext';
import ChunkedVoiceRecorder from '../utils/recordNoteUtil';
import AudioVisualizer from '../utils/AudioVisualizer';

const recordingErrorKeys = new Set([
  'recording.startError', 'recording.microphoneError', 'recording.microphoneLost',
  'recording.uploadError', 'recording.storageError', 'recording.stopError',
  'recording.sessionError', 'recording.unsupported',
  'recording.noAudio', 'recording.sizeLimit'
]);

const errorKey = (error, fallback) => recordingErrorKeys.has(error?.message) ? error.message : fallback;

const formatTime = (timeInMs) => {
  const totalSeconds = Math.floor(timeInMs / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

function NoteTaker() {
  const { t } = useTranslation();
  const { saving, ready, error: preferencesError, retry } = usePreferences();
  const note = useNote();
  const noteRef = useRef(note);
  noteRef.current = note;

  const [state, setState] = useState('idle');
  const [hasSession, setHasSession] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const [recordingNotice, setRecordingNotice] = useState('');
  const [time, setTime] = useState(0);
  const [audioStream, setAudioStream] = useState(null);
  const [operation, setOperation] = useState('');
  const [recoverableSessions, setRecoverableSessions] = useState([]);
  const [recoveryLoaded, setRecoveryLoaded] = useState(false);
  const [recoveryScanFailed, setRecoveryScanFailed] = useState(false);
  const recorderRef = useRef(null);
  const activeSessionRef = useRef(null);
  const operationRef = useRef(false);
  const mountedRef = useRef(true);

  const initializeRecorder = useCallback(() => {
    if (!recorderRef.current) {
      const recorder = new ChunkedVoiceRecorder({
        onStateChange: nextState => {
          if (!mountedRef.current || recorderRef.current !== recorder) return;
          const sessionActive = Boolean(recorder.sessionId) && nextState !== 'idle';
          setState(nextState);
          setHasSession(sessionActive);
          if (recorder.sessionId) activeSessionRef.current = recorder.sessionId;
          noteRef.current.setIsRecording(sessionActive);
          noteRef.current.setIsRecordingActive(nextState === 'recording');
          if (sessionActive) noteRef.current.setIsSessionActive(true);
          if (nextState === 'idle') {
            const completedSessionId = activeSessionRef.current;
            activeSessionRef.current = null;
            setRecoverableSessions(sessions => sessions.filter(session => session.sessionId !== completedSessionId));
            setTime(0);
            setAudioStream(null);
            setRecordingNotice('');
          }
        },
        onError: error => {
          if (mountedRef.current && recorderRef.current === recorder) {
            if (error?.message === 'recording.fallbackNotice') {
              setRecordingNotice('recording.fallbackNotice');
            } else {
              if (error?.message === 'recording.noAudio' && !noteRef.current.hasAnyPendingNotes?.()) {
                noteRef.current.setIsSessionActive(false);
              }
              setRecordingError(errorKey(error, 'recording.sessionError'));
            }
          }
        },
        onStreamReady: stream => {
          if (mountedRef.current && recorderRef.current === recorder) setAudioStream(stream);
        },
        onTick: elapsed => {
          if (mountedRef.current && recorderRef.current === recorder) setTime(elapsed);
        }
      });
      recorderRef.current = recorder;
    }
    return recorderRef.current;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      recorder?.dispose();
      noteRef.current.setIsRecording(false);
      noteRef.current.setIsRecordingActive(false);
    };
  }, []);

  const scanRecoverableSessions = useCallback(async () => {
    const recorder = initializeRecorder();
    setRecoveryLoaded(false);
    setRecoveryScanFailed(false);
    try {
      const sessions = await recorder.findRecoverableSessions();
      if (!mountedRef.current || recorderRef.current !== recorder) return;
      setRecoverableSessions(sessions);
      setRecoveryLoaded(true);
      setRecordingError(previous => previous === 'recording.sessionError' ? '' : previous);
    } catch (error) {
      if (!mountedRef.current || recorderRef.current !== recorder) return;
      setRecoveryScanFailed(true);
      setRecordingError(errorKey(error, 'recording.sessionError'));
    }
  }, [initializeRecorder]);

  useEffect(() => {
    if (ready) scanRecoverableSessions();
  }, [ready, scanRecoverableSessions]);

  useEffect(() => {
    const handleUnload = event => {
      if (note.isSessionActive || recoverableSessions.length) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [note.isSessionActive, recoverableSessions.length]);

  const runOperation = async (kind, action, fallback) => {
    if (operationRef.current) return;
    operationRef.current = true;
    setOperation(kind);
    setRecordingError('');
    try {
      await action(initializeRecorder());
    } catch (error) {
      if (mountedRef.current) {
        setRecordingError(errorKey(error, fallback));
      }
    } finally {
      operationRef.current = false;
      if (mountedRef.current) setOperation('');
    }
  };

  const busy = Boolean(operation) || state === 'starting' || state === 'stopping';
  const recordingActive = state === 'recording';
  const canResume = Boolean(recorderRef.current?.canResume && ['paused', 'error'].includes(state));

  const handleStart = () => {
    if (busy || !ready || saving || hasSession || !recoveryLoaded || recoverableSessions.length) return;
    runOperation('start', async recorder => {
      if (!window.confirm(t('recording.consent'))) return;
      await recorder.start();
    }, 'recording.startError');
  };

  const handleRecovery = sessionId => runOperation('recover', async recorder => {
    await recorder.recoverSession(sessionId, noteRef.current.setLatestNote, noteRef.current.addLoadingNote);
    if (mountedRef.current) setRecoverableSessions(sessions => sessions.filter(session => session.sessionId !== sessionId));
  }, 'recording.stopError');

  return (
    <div className="note-taker-container">
      {preferencesError && <p role="alert">{t(preferencesError)} <button onClick={retry}>{t('common.retry')}</button></p>}
      {recordingError && <p role="alert">
        {t(recordingError)}
        {recoveryScanFailed && <button onClick={scanRecoverableSessions} disabled={busy}>{t('common.retry')}</button>}
      </p>}
      {recordingNotice && <p role="status">{t(recordingNotice)}</p>}
      {recoverableSessions.map(session => <p key={session.sessionId}>
        {t('recording.recoveryAvailable')}{' '}
        <button onClick={() => handleRecovery(session.sessionId)} disabled={busy || hasSession}>
          {t('recording.retrySave')}
        </button>
      </p>)}
      {(state === 'stopping' || operation === 'recover') && <p role="status">
        {t(operation === 'recover' ? 'recording.recovering' : 'recording.saving')}
      </p>}
      <div className="title-visualizer-timer-wrapper">
        <img src="/mic_image.png" height="50px" width="50px" alt={t('recording.microphone')} />
        <div className="visualizer-container">
          <AudioVisualizer stream={audioStream} isRecording={hasSession} isRecordingActive={recordingActive} />
        </div>
        <div className="timer-container"><span id="timer">{formatTime(time)}</span></div>
      </div>
      <div className="button-grid">
        <button className={recordingActive ? 'active' : ''} onClick={handleStart}
          disabled={busy || saving || !ready || hasSession || !recoveryLoaded || Boolean(recoverableSessions.length)}>
          {t('recording.start')}
        </button>
        <button className={state === 'paused' ? 'active' : ''}
          onClick={() => runOperation('pause', recorder => recorder.pauseRecording(), 'recording.microphoneError')}
          disabled={busy || !recordingActive}>{t('recording.pause')}</button>
        <button onClick={() => runOperation('resume', recorder => recorder.resumeRecording(), 'recording.microphoneError')}
          disabled={busy || !canResume}>{t('recording.resume')}</button>
        <button onClick={() => runOperation('stop', recorder => recorder.stop(noteRef.current.setLatestNote, noteRef.current.addLoadingNote), 'recording.stopError')}
          disabled={busy || !hasSession}>
          {t(state === 'error' && !canResume && hasSession ? 'recording.retryStop' : 'recording.stop')}
        </button>
      </div>
    </div>
  );
}

export default NoteTaker;
